<template>
	<div class="cards-picker">

		<div class="card-select-section">
			<div class="card-select-section-row">
				<div class="card-select-filter-container">
					<div
						class="card-select-item"
						v-for="(card) in cards"
						:class="{active: card.card_id === cardsStore.activeCardId}"
						:key="card.card_id"
						@click="cardsStore.changeCard(card.card_id)"
					><p>{{ card.name }}</p></div>
				</div>
			</div>
			<div class="card-select-action" @click="clickAddNewCard">
				<img :src="'https://img.qromo.io/img/vue/icons/icon-plus'+(userStore.darkMode ? '-dark' : '')+'.svg'" class="icon-dimension-15"/>
			</div>
		</div>

		<div class="no-cards" v-if="!cards || cards.length === 0">
			<div class="no-cards-text">{{ getString('no_cards_created') }}</div>
			<div class="no-cards-subtitle">{{ getString('no_cards_created_explain') }}</div>
		</div>

		<AppBottomSheet :active="showNewCardBottomSheet" :force-width="bottomSheetWidth" @toggling-popup="bottomSheetToggled">
			<ModalHeader :title="getString('new_card')" :button-text="getString('add')" @click-button="confirmNewCard" :button-loading="loadingNewCard" :button-disabled="showBannerMaxPhysical || showBannerMaxVirtual" />
			<div class="banner-deadline warning" :class="{dark: userStore.darkMode}" v-if="showBannerMaxVirtual">
				<div class="banner-deadline-icon" v-html="svgStore.get('iconWarning')"></div>
				<div class="banner-deadline-text">
					{{ getString('max_number_issuing_virtual') }}
				</div>
				<div class="banner-deadline-cta">
					<AppButton :text="getString('buy_more')" :loading="loadingBuy" @click="buyCards" inverted />
				</div>
			</div>
			<div class="banner-deadline warning" :class="{dark: userStore.darkMode}" v-if="showBannerMaxPhysical">
				<div class="banner-deadline-icon" v-html="svgStore.get('iconWarning')"></div>
				<div class="banner-deadline-text">
					{{ getString('max_number_issuing_physical') }}
				</div>
				<div class="banner-deadline-cta">
					<AppButton :text="getString('buy_more')" :loading="loadingBuy" @click="buyCards" inverted />
				</div>
			</div>
			<div class="cards-columns">
				<div class="card-container" :style="{ '--card-color': cardsStore.availableColors[newCard.color] }">
					<CardViewer class="card-viewer-container" last-four-digits="0000" :label="newCard.name" :color="newCard.color" />
				</div>
				<div class="options-container">
					<AppList :items="optionsItems">
						<template #type-slot="{ item }">
							<div class="row centered space-between">
								<p class="list-item-title">{{ item.title }}</p>
								<AppTabsSwitch :items="[getString('virtual'), getString('physical')]" auto-switch @switched="switchedType" />
							</div>
						</template>
						<template #color-slot="{ item }">
							<div class="heading">
								<div class="heading-title">{{ getString('pick_a_color') }}</div>
								<div class="colors-select">
									<div class="color-select-item"
										v-for="(color) in virtualColors"
										:key="color"
										:class="{active: newCard.color === color}"
										@click="newCard.color = color"
										:style="{ '--color-select': cardsStore.availableColors[color] }" >
									</div>
								</div>
							</div>
						</template>
						<template #input-address="{item}">
							<div class="row centered space-between list-row">
								<p class="list-item-title">{{ getString('address') }}</p>
								<AppGoogleMapsInput class="text-input" no-map :placeholder="getString('shipping_address')" @changed-address="(a) => newCard.shippingAddress = a" />
							</div>
						</template>
					</AppList>
				</div>
			</div>
		</AppBottomSheet>

		<AppBottomSheet :active="showCardsPickerBottomSheet" :force-width="bottomSheetWidthSmall" @toggling-popup="bottomSheetCardsToggled">
			<div class="cards-picker-bottom-sheet" v-if="showCardsPickerBottomSheet">
				<ModalHeader :title="getString('select_cardholder')" />
				<CardholderPicker @select-cardholder="selectCardholder" />
			</div>
		</AppBottomSheet>

		<CheckoutInPlace :client-secret="clientSecret" @toggling-popup="togglingPopupSheet" @checkout-complete="paymentComplete" />

	</div>
</template>

<script>
import {useCardsStore} from "@/console-new/stores/cards.store";
import {useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppList from "@/shared/components/AppList.vue";
import AppButton from "@/shared/components/AppButton.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import CardViewer from "@/console-new/components/config/administration/cards/CardViewer.vue";
import AppTabsSwitch from "@/shared/components/AppTabsSwitch.vue";
import CardholderPicker from "@/console-new/components/config/administration/cards/CardholderPicker.vue";
import AppGoogleMapsInput from "@/shared/components/AppGoogleMapsInput.vue";
import API from "@/shared/helpers/API";
import CheckoutInPlace from "@/console-new/components/shared/CheckoutInPlace.vue";

export default {
	setup() {
		const cardsStore = useCardsStore();
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const svgStore = useSvgStore();
		return {
			cardsStore,
			getString: languageStore.getString,
			userStore,
			svgStore,
			notificationStore
		}
	},
	name: "CardsPicker",
	mixins: [WindowWidth],
	components: {
		CheckoutInPlace,
		AppGoogleMapsInput,
		AppTabsSwitch,
		CardViewer,
		ModalHeader,
		AppBottomSheet,
		AppList,
		CardholderPicker,
		AppButton
	},
	data() {
		return {
			showNewCardBottomSheet: false,
			showCardsPickerBottomSheet: false,
			loadingNewCard: false,
			loadingBuy: false,
			newCard: {
				name: this.getString('card_placeholder'),
				color: "purple",
				type: "virtual",
				cardholder: null,
				shippingAddress: null,
			},
			clientSecret: null,
		}
	},
	computed: {
		showBannerMaxVirtual() {
			if(this.newCard.type === "virtual") {
				return this.userStore.currBusiness?.issuing_max_virtual <= this.cardsStore.totalCardsVirtual;
			}
			return false;
		},
		showBannerMaxPhysical() {
			console.log("Checking max physical cards", this.newCard.type, this.userStore.currBusiness?.issuing_max_physical, this.cardsStore.totalCardsPhysical);
			if(this.newCard.type === "physical") {
				return this.userStore.currBusiness?.issuing_max_physical <= this.cardsStore.totalCardsPhysical;
			}
			return false;
		},
		virtualColors() {
			return Object.keys(this.cardsStore.availableColors).filter(color => {
				return color !== "black";
			});
		},
		optionsItems() {
			let items = [
				{
					title: this.getString('card_type'),
					customSlot: 'type-slot',
				}
			];
			if(this.newCard.type === "virtual") {
				items.push({
					title: this.getString('pick_a_color'),
					customSlot: 'color-slot',
				});
			} else {
				items.push({
					title: this.getString("shipping_address"),
					customSlot: 'input-address',
				});
			}
			items.push({
				title: this.getString('card_name'),
				placeholder: this.getString('card_placeholder'),
				textInput: true,
				callbackText: v => { this.newCard.name = v; },
			});
			items.push({
				title: this.getString('cardholder'),
				buttonTitle: this.newCard.cardholder ? this.newCard.cardholder.name : this.getString('select'),
				callbackButton: () => {
					this.showCardsPickerBottomSheet = true;
				},
			});
			return items;
		},
		cards() {
			return this.cardsStore.businessCards ? this.cardsStore.businessCards : [];
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "750px";
			} else {
				return "960px";
			}
		},
		bottomSheetWidthSmall() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "750px";
			}
		}
	},
	methods: {
		togglingPopupSheet(v) {
			if(!v) {
				this.clientSecret = null;
			}
		},
		paymentComplete() {
			setTimeout(() => {
				this.userStore.refreshBusiness(this.userStore.currBusinessId);
			}, 2000);
			setTimeout(() => {
				this.clientSecret = null;
			}, 2500);
		},
		buyCards() {

			this.loadingBuy = true;
			API.init().stripeBuyIssuingCards(this.userStore.currBusinessId, this.newCard.type, (response) => {
				this.loadingBuy = false;
				if (response && response.result === "OK" && response.client_secret) {
					this.clientSecret = response.client_secret;
				} else {
					this.notificationStore.showNotification("generic-fail");
				}
			});

		},
		clickAddNewCard() {
			this.showNewCardBottomSheet = true;
		},
		bottomSheetToggled(isActive) {
			if(!isActive) {
				this.showNewCardBottomSheet = false;
			}
		},
		bottomSheetCardsToggled(isActive) {
			if(!isActive) {
				this.showCardsPickerBottomSheet = false;
			}
		},
		switchedType(index) {
			if(index === 0) {
				this.newCard.type = "virtual";
				this.newCard.color = this.virtualColors[0];
			} else {
				this.newCard.type = "physical";
				this.newCard.color = "black";
			}
		},
		selectCardholder(cardholder) {
			this.newCard.cardholder = cardholder;
			this.showCardsPickerBottomSheet = false;
		},
		confirmNewCard() {

			if(!this.newCard.name || this.newCard.name.length < 3) {
				this.notificationStore.showNotification("invalid-name");
				return;
			}
			if(!this.newCard.cardholder || !this.newCard.cardholder.cardholder_id) {
				this.notificationStore.showNotification("invalid-cardholder");
				return;
			}
			if(this.newCard.type !== "virtual" && !this.newCard.shippingAddress) {
				this.notificationStore.showNotification("error-address");
				return;
			}

			this.loadingNewCard = true;
			API.init().newCard(this.userStore.currBusinessId, this.newCard.name, this.newCard.cardholder?.cardholder_id, this.newCard.type, this.newCard.color, JSON.stringify(this.newCard.shippingAddress), (res) => {
				this.loadingNewCard = false;
				if(res && res.result === "OK") {
					this.cardsStore.businessCards = null;
					this.showNewCardBottomSheet = false;
					this.newCard = {
						name: this.getString('card_placeholder'),
						color: "purple",
						type: "virtual",
						cardholder: null,
						shippingAddress: null,
					};
					this.cardsStore.getBusinessCards();
				} else if(res.details && res.details === "invalid_address") {
					this.notificationStore.showNotification("error-address");
				} else if(res.details && res.details === "invalid_cardholder") {
					this.notificationStore.showNotification("invalid-cardholder");
				} else if(res.details && res.details === "no_active_plan") {
					this.notificationStore.showNotification("no-active-plan");
				} else if(res.details && res.details === "no_transactions") {
					this.notificationStore.showNotification("no-transactions");
				} else if(res.details && res.details === "reached_max_cards") {
					this.notificationStore.showNotification("reached-max-cards");
					if(this.newCard.type === "virtual") {
						this.cardsStore.totalCardsVirtual = 999;
					} else {
						this.cardsStore.totalCardsPhysical = 999;
					}
				} else {
					this.notificationStore.showNotification("generic-fail");
				}
			});
		}
	}
}
</script>

<style scoped lang="scss">
.card-select-section {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	position: relative;
	padding: 10px;
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
	border-radius: 0 0 13px 13px;
	background: rgba(var(--rgb-bg-primary-1), 67%);
	box-shadow: inset -3px -3px 6px var(--color-box-shadow-2);
	gap: 10px;
	margin-bottom: var(--main-padding);


	.card-select-action {
		border-radius: 13px;
		border: 1px solid $primary;
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 9px;
		transition: background-color 0.25s ease-in-out;
		cursor: pointer;
		flex-shrink: 0;

		img {
			width: 16px;
			height: 16px;
		}

	}

	.card-select-section-row {
		display: flex;
		flex-direction: row;
		align-items: center;
		overflow-x: auto;
		-ms-overflow-style: none;
		scrollbar-width: none;
		border-radius: 13px;
		background-color: var(--color-bg-primary-2);
		box-shadow: inset 2px 2px 3px var(--color-box-shadow-3);
		min-height: 51px;
		flex-grow: 1;

		.card-select-filter-container {
			border-radius: 13px;
			display: flex;
			flex-direction: row;
			justify-content: flex-start;
			align-items: center;
			padding: 8px;
			gap: 6px;
			width: 100%;
			height: 66px;

			.card-select-item {
				border-radius: 13px;
				border: 1px solid var(--color-primary);
				padding: 7px 12px;
				width: fit-content;
				cursor: pointer;
				user-select: none;
				transition: background-color 0.3s ease;
				display: flex;
				align-items: center;
				justify-content: center;
				height: 50px;
				flex: 1;
				min-width: 140px;
				max-width: 250px;

				p {
					@include truncateString(1);
					font-size: 15px;
					color: var(--color-primary);
				}

				&.active {
					background-color: var(--color-active-button);

					p {
						color: var(--color-active-button-text);
					}
				}
			}

			@media (max-width: 900px) {
				padding: 8px;
				gap: 8px;
				min-height: 56px;

				.card-select-item {
					padding: 7px 8px;
				}
			}

		}

	}
}

.no-cards {
	width: calc(100% - var(--orders-padding) * 2);
	background-color: var(--color-bg);
	border-radius: 13px;
	border: 2px dashed $primary;
	padding: 20px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	min-height: 130px;
	gap: 6px;
	margin: var(--orders-padding);
	z-index: 0;
	position: relative;

	.no-cards-text {
		font-size: 18px;
		color: $primary;
		font-weight: 700;
	}

	.no-cards-subtitle {
		font-size: 14px;
		color: $primary;
		font-weight: 400;
	}
}
.cards-columns {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: stretch;
	width: 100%;

	.card-container {
		width: 50%;
		border-radius: 13px;
		box-shadow: -2px -2px 10px 0px var(--color-box-shadow-2) inset, 2px 2px 20px 0px var(--color-box-shadow-2) inset;
		min-height: 270px;
		display: flex;
		justify-content: center;
		align-items: center;
		position: relative;
		--card-color: #0752A3;

		&:after {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			border-radius: 13px;
			opacity: 0.15;
			background-color: var(--card-color);
		}

		&.dark {
			&:after {
				opacity: 0.5;
			}
		}

		.card-viewer-container {
			position: relative;
			z-index: 1;
			padding: 20px;
		}
	}

	.options-container {
		width: 50%;

		.heading {
			display: flex;
			flex-direction: column;
			justify-content: flex-start;
			align-items: flex-start;
			width: 100%;
			height: 100%;

			.heading-title {
				margin-bottom: 10px;
			}

			.colors-select {
				display: flex;
				flex-direction: row;
				justify-content: space-between;
				align-items: center;
				flex-wrap: wrap;
				gap: 10px;
				width: 100%;
				margin-bottom: 4px;

				.color-select-item {
					width: 28px;
					height: 28px;
					border-radius: 50%;
					cursor: pointer;
					position: relative;
					background-color: var(--color-select);

					&.active {

						&:after {
							content: '';
							position: absolute;
							top: -4px;
							left: -4px;
							width: calc(100% + 4px);
							height: calc(100% + 4px);
							border-radius: 50%;
							border: 2px solid var(--color-select);
						}

					}

				}
			}
		}
	}

	@media (max-width: 767px) {
		flex-direction: column;
		gap: 10px;

		.card-container {
			width: 100%;
		}

		.options-container {
			width: 100%;
		}
	}

	@media (max-width: 380px) {
		.card-container {
			box-shadow: unset;

			&:after {
				display: none;
			}

			&:deep(.card-face) {
				box-shadow: none;
			}

			.card-viewer-container {
				padding: 0;
			}
		}
	}

}
.list-row {
	gap: 10px;
	.map-input-container {
		margin-left: auto;
		&:deep(.address-input) {
			width: 240px;

			@media (max-width: 1250px) {
				width: 190px;
			}
		}
	}
}


.banner-deadline {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 20px;
	border-radius: 13px;
	margin-bottom: 20px;
	margin-top: 20px;
	cursor: pointer;

	&.warning {
		background-color: rgba(253, 199, 0, 0.95);
	}

	&.dark {

		&.warning {
			background-color: rgba(253, 199, 0, 0.55);
		}

		&.blocked {
			background-color: rgba(252, 34, 145, 0.5);
		}

	}

	&:deep(svg) {
		width: 20px;
		height: 20px;
		margin-right: 10px;

		.fill {
			fill: var(--color-txt-white);
		}

		.stroke {
			stroke: var(--color-txt-white);
		}
	}

	.banner-deadline-text {
		color: var(--color-txt-white);
		font-size: 14px;
		line-height: 18px;
		flex-grow: 1;
	}

	.banner-deadline-cta {
		margin-left: auto;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}
}
</style>